import {
  type PropsWithChildren,
  type Ref,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { Flex } from '../../../layouts/flex/Flex.js';
import { ColorRule } from '../../core/slots/ColorRule.js';
import type { ChartDatapoint } from '../../core/types/chart-data.js';
import { getFormatter } from '../../core/utils/get-formatter.js';
import { makeAccessor } from '../../core/utils/makeAccessor.js';
import type { XYChartRef } from '../../xy-chart/types/xy-chart.js';
import { XYChart } from '../../xy-chart/XYChartRoot.js';
import { FlameIsolationNav } from '../shared/components/isolation-nav/FlameIsolationNav.js';
import { IsolateSegmentAction } from '../shared/components/tooltip/IsolateSegmentAction.js';
import {
  DEFAULT_FLAME_CHART_UNIT,
  DEPTH_FIELD,
  DISPLAY_NAME_FLAME_CHART,
  END_FIELD,
  FLAME_CHART_HEIGHT_OVERHEAD_PX,
  FRAME_HEIGHT_PX,
  START_FIELD,
  VALUE_FIELD,
  XY_DEFAULTS,
} from '../shared/constants.js';
import { scrollContainer } from '../shared/Flame.sty.js';
import { useFlameData } from '../shared/hooks/use-flame-data.js';
import { useFlameHeight } from '../shared/hooks/use-flame-height.js';
import { useFlameIsolateSpan } from '../shared/hooks/use-flame-isolate-span.js';
import { useFlameOnEvent } from '../shared/hooks/use-flame-on-event.js';
import { useFlameIsolationState } from '../shared/hooks/use-isolation-state.js';
import { FlameChartTooltipSlot as Tooltip } from '../shared/slots/Tooltip.js';
import type { FlameChartProps, FlameRef } from '../shared/types/flame-chart.js';
import { orderSpansHierarchically } from '../shared/utils/order-spans-hierarchically.js';
import { resolveFlameTooltipContent } from '../shared/utils/resolve-flame-tooltip-content.js';
import { DefaultTooltip } from './components/tooltip/DefaultTooltip.js';
import { iterateSlots } from './utils/iterate-slots.js';

/**
 * @internal
 */
function FlameChartRoot<TData extends ChartDatapoint = ChartDatapoint>(
  {
    data,
    idAccessor,
    parentIdAccessor,
    startAccessor,
    endAccessor,
    selfValueAccessor,
    labelAccessor,
    colorPalette,
    orientation = 'bottom-up',
    truncationMode,
    formatter,
    unit,
    minTime,
    currentZoom,
    seriesActions,
    height,
    loading,
    __globalActions,
    onClick,
    children,
  }: PropsWithChildren<FlameChartProps<TData>>,
  ref: Ref<FlameRef>,
) {
  const isolationEnabled = __globalActions === true;
  const { isolatedId, isolate, goBack, goForward, canGoBack, canGoForward } =
    useFlameIsolationState();

  const resolvedFormatter = useMemo(() => getFormatter(formatter), [formatter]);

  const {
    isolatedRoot,
    effectiveData,
    visibleRootId,
    childrenByParentId,
    byId,
  } = useFlameData({
    data,
    idAccessor,
    parentIdAccessor,
    isolatedId,
    isolationEnabled,
  });

  const isolateSpan = useFlameIsolateSpan({
    isolationEnabled,
    byId,
    isolatedId,
    isolate,
  });

  useImperativeHandle(ref, () => ({ isolate: isolateSpan }), [isolateSpan]);

  const orderedData = useMemo(() => {
    const offset = isolatedRoot
      ? Number(makeAccessor(startAccessor)(isolatedRoot) ?? 0)
      : (minTime ?? 0);

    return orderSpansHierarchically(effectiveData, {
      spanIdAccessor: idAccessor,
      parentIdAccessor,
      x0Accessor: startAccessor,
      x1Accessor: endAccessor,
      offset,
      selfValueAccessor,
    });
  }, [
    effectiveData,
    isolatedRoot,
    idAccessor,
    parentIdAccessor,
    startAccessor,
    endAccessor,
    minTime,
    selfValueAccessor,
  ]);

  const normalizedZoom = useMemo<[number, number] | undefined>(() => {
    if (!currentZoom) {
      return undefined;
    }
    const offset = minTime ?? 0;
    return [currentZoom[0] - offset, currentZoom[1] - offset];
  }, [currentZoom, minTime]);

  const globalActions = useMemo(() => {
    if (!isolationEnabled) {
      return undefined;
    }
    return (datapoint: TData) => {
      const id = makeAccessor(idAccessor)(datapoint);
      return [
        <IsolateSegmentAction
          key="isolate"
          onSelect={() => isolateSpan(id)}
          disabled={id === visibleRootId}
        />,
      ];
    };
  }, [isolationEnabled, idAccessor, isolateSpan, visibleRootId]);

  const { customTooltipTemplate, colorRules } = iterateSlots<TData>(children);

  const xyRef = useRef<XYChartRef>(null);
  const handleScroll = useCallback(() => {
    xyRef.current?._clearTooltip();
  }, []);

  const depthCount = useMemo(() => {
    let max = 0;
    for (const d of orderedData) {
      const depth = Number(d[DEPTH_FIELD] ?? 0);
      if (depth > max) {
        max = depth;
      }
    }
    return orderedData.length === 0 ? 0 : max + 1;
  }, [orderedData]);

  const { computedHeight, shouldScroll, scrollSectionHeight } = useFlameHeight(
    height,
    depthCount,
    FLAME_CHART_HEIGHT_OVERHEAD_PX,
  );

  const handleOnEvent = useFlameOnEvent({
    onClick,
    data,
    idAccessor,
    childrenByParentId,
  });

  const chart = (
    <XYChart
      ref={xyRef}
      data={orderedData}
      truncationMode={truncationMode}
      height={computedHeight}
      __onEvent={handleOnEvent}
      loading={loading}
    >
      <XYChart.RectSeries
        x0Accessor={START_FIELD}
        x1Accessor={END_FIELD}
        y0Accessor={DEPTH_FIELD}
        valueAccessor={VALUE_FIELD}
        colorPalette={colorPalette}
        _labelAccessor={labelAccessor}
        xAxisId={XY_DEFAULTS.xAxisId}
        yAxisId={XY_DEFAULTS.yAxisId}
      />
      <XYChart.XAxis
        id={XY_DEFAULTS.xAxisId}
        type="numerical"
        position={orientation === 'bottom-up' ? 'bottom' : 'top'}
        formatter={formatter}
        unit={unit ?? DEFAULT_FLAME_CHART_UNIT}
        currentZoom={isolatedRoot !== null ? undefined : normalizedZoom}
        hideCrosshair
      />
      <XYChart.YAxis
        id={XY_DEFAULTS.yAxisId}
        type="categorical"
        position="left"
        hidden
        reversed={orientation === 'bottom-up'}
        hideCrosshair
        _fixedBandSize={FRAME_HEIGHT_PX}
      />
      <XYChart.Legend hidden />
      <XYChart.Toolbar hidden />
      <XYChart.Select disabled />
      {colorRules}
      <XYChart.Tooltip>
        {(payload) =>
          resolveFlameTooltipContent(
            payload,
            data,
            customTooltipTemplate,
            () => (
              <DefaultTooltip<TData>
                payload={payload}
                data={data}
                labelAccessor={labelAccessor}
                selfValueAccessor={selfValueAccessor}
                formatter={resolvedFormatter}
                unit={unit ?? DEFAULT_FLAME_CHART_UNIT}
                seriesActions={seriesActions}
                globalActions={globalActions}
              />
            ),
          )
        }
      </XYChart.Tooltip>
      <XYChart.Select disabled />
    </XYChart>
  );

  const scrollableChart = (
    <section
      className={scrollContainer}
      style={{ height: scrollSectionHeight }}
      onScroll={handleScroll}
      aria-label="Scroll section for Flame Chart"
    >
      {chart}
    </section>
  );

  if (!isolationEnabled) {
    return shouldScroll ? scrollableChart : chart;
  }

  return (
    <Flex flexDirection="column" gap={8}>
      <FlameIsolationNav
        canGoBack={canGoBack}
        canGoForward={canGoForward}
        onGoBack={goBack}
        onGoForward={goForward}
      />
      {scrollableChart}
    </Flex>
  );
}

FlameChartRoot.displayName = DISPLAY_NAME_FLAME_CHART;

/**
 * @internal
 */
export const FlameChart = Object.assign(
  /* @__PURE__ */ forwardRef(FlameChartRoot),
  {
    Tooltip,
    ColorRule,
  },
);

(FlameChart as typeof FlameChart & { displayName: string }).displayName =
  DISPLAY_NAME_FLAME_CHART;
